import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import toast from "react-hot-toast";
import { auth } from "../lib/firebase";
import { createArticle, uploadArticleFile } from "../lib/articles";
import { MARKDOWN_TEMPLATE, parseArticleMarkdown } from "../lib/markdownImport";

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const BLOCK_TYPES = [
  "heading",
  "subheading",
  "paragraph",
  "list",
  "code",
  "table",
  "image",
  "callout",
];

const emptyBlock = (type) => {
  if (type === "list") return { type, items: [""] };
  if (type === "table") return { type, headers: "", rowsText: "" };
  if (type === "image") return { type, file: null, url: "", caption: "" };
  return { type, text: "" };
};

// Converts a block parsed from markdown into the same shape the manual
// block-editing UI uses (so imported blocks stay editable afterwards).
const toEditableBlock = (block) => {
  if (block.type === "list") return { type: "list", items: block.items };
  if (block.type === "table") {
    return {
      type: "table",
      headers: block.headers.join(" | "),
      rowsText: block.rows.map((r) => r.cells.join(" | ")).join("\n"),
    };
  }
  if (block.type === "image") {
    return { type: "image", file: null, url: block.url, caption: block.caption || "" };
  }
  return { type: block.type, text: block.text };
};

const DEFAULT_ACCENT = "#99DFAF";

const AdminArticleEditor = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [author, setAuthor] = useState("Nodefleet Team");
  const [readingTime, setReadingTime] = useState("3 min read");
  const [tagsInput, setTagsInput] = useState("");
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [coverFile, setCoverFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [blocks, setBlocks] = useState([emptyBlock("paragraph")]);
  const [saving, setSaving] = useState(false);
  const markdownInputRef = useRef(null);

  const handleTitleChange = (value) => {
    setTitle(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const handleMarkdownFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseArticleMarkdown(text);

      if (parsed.title) handleTitleChange(parsed.title);
      if (parsed.slug) {
        setSlugTouched(true);
        setSlug(slugify(parsed.slug));
      }
      if (parsed.excerpt) setExcerpt(parsed.excerpt);
      if (parsed.author) setAuthor(parsed.author);
      if (parsed.readingTime) setReadingTime(parsed.readingTime);
      if (parsed.tags) setTagsInput(parsed.tags.join(", "));
      if (parsed.accent) setAccent(parsed.accent);
      setBlocks(parsed.content.map(toEditableBlock));

      toast.success(`Imported ${parsed.content.length} blocks from markdown.`);
    } catch (err) {
      toast.error("Couldn't read that markdown file.");
    } finally {
      if (markdownInputRef.current) markdownInputRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([MARKDOWN_TEMPLATE], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "article-template.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateBlock = (index, next) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? next : b)));
  };

  const addBlock = (type) => setBlocks((prev) => [...prev, emptyBlock(type)]);

  const removeBlock = (index) =>
    setBlocks((prev) => prev.filter((_, i) => i !== index));

  const moveBlock = (index, dir) => {
    setBlocks((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate("/admin/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !slug.trim() || !excerpt.trim()) {
      toast.error("Title, slug and excerpt are required.");
      return;
    }
    setSaving(true);
    try {
      const [coverImageUrl, pdfUrl] = await Promise.all([
        coverFile ? uploadArticleFile(coverFile, slug, "cover") : Promise.resolve(""),
        pdfFile ? uploadArticleFile(pdfFile, slug, "pdf") : Promise.resolve(""),
      ]);

      const content = [];
      for (const block of blocks) {
        if (block.type === "image") {
          // A new file always wins; otherwise fall back to an already-hosted
          // URL (e.g. one imported from markdown that doesn't need re-upload).
          if (block.file) {
            const url = await uploadArticleFile(block.file, slug, "images");
            content.push({ type: "image", url, caption: block.caption || "" });
          } else if (block.url) {
            content.push({ type: "image", url: block.url, caption: block.caption || "" });
          }
        } else if (block.type === "list") {
          const items = block.items.map((i) => i.trim()).filter(Boolean);
          if (items.length) content.push({ type: "list", items });
        } else if (block.type === "table") {
          const headers = block.headers.split("|").map((h) => h.trim()).filter(Boolean);
          // Firestore disallows arrays-of-arrays, so each row is wrapped: {cells: [...]}.
          const rows = block.rowsText
            .split("\n")
            .map((r) => r.trim())
            .filter(Boolean)
            .map((r) => ({ cells: r.split("|").map((c) => c.trim()) }));
          if (headers.length && rows.length) content.push({ type: "table", headers, rows });
        } else if (block.text.trim()) {
          content.push({ type: block.type, text: block.text.trim() });
        }
      }

      const tags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const theme = {
        name: slug,
        accent,
        accentSoft: accent,
        bg: "#12111d",
        surface: `${accent}12`,
        border: `${accent}30`,
        coverGradient: coverImageUrl
          ? ""
          : `radial-gradient(circle at 30% 20%, ${accent}33, transparent 60%), linear-gradient(160deg, #12111d 0%, #0A0A14 100%)`,
      };

      await createArticle({
        slug,
        title: title.trim(),
        date: new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        author,
        readingTime,
        tags,
        theme,
        coverImageUrl,
        pdfUrl,
        pdfViewUrl: pdfUrl,
        excerpt: excerpt.trim(),
        content,
      });

      toast.success("Article published.");
      navigate(`/articles/${slug}`);
    } catch (err) {
      toast.error(err.message || "Failed to publish article.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-space px-6 py-16 text-white">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">
            New <span className="text-brand-gradient italic">article</span>
          </h1>
          <button
            type="button"
            onClick={handleSignOut}
            className="rounded-full border border-white/20 px-4 py-2 text-sm text-gray-300 transition-colors hover:border-white/40 hover:text-white"
          >
            Sign out
          </button>
        </div>

        {/* Markdown import — write the whole article once as a .md file
            (metadata + content) and load it here instead of building it
            block by block. Everything it fills in stays editable below. */}
        <section className="mt-10 rounded-2xl border border-primary/30 bg-primary/[0.06] p-6">
          <h2 className="text-lg font-semibold text-white">Import from Markdown</h2>
          <p className="mt-1 text-sm text-gray-400">
            Upload a .md file to fill in the fields and content blocks below automatically.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              ref={markdownInputRef}
              type="file"
              accept=".md,.markdown,text/markdown"
              onChange={(e) => handleMarkdownFile(e.target.files?.[0] || null)}
              className="text-gray-400"
            />
            <button
              type="button"
              onClick={downloadTemplate}
              className="rounded-full border border-white/20 px-4 py-1.5 text-sm text-gray-300 transition-colors hover:border-primary/40 hover:text-primary"
            >
              Download .md template
            </button>
          </div>
        </section>

        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-8">
          {/* Metadata */}
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <h2 className="text-lg font-semibold text-white">Metadata</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm text-gray-300 sm:col-span-2">
                Title
                <input
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  required
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white outline-none focus:border-primary/60"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-300 sm:col-span-2">
                Slug (used in /articles/…)
                <input
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(slugify(e.target.value));
                  }}
                  required
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white outline-none focus:border-primary/60"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-300 sm:col-span-2">
                Excerpt (shown on cards + meta description)
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  required
                  rows={2}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white outline-none focus:border-primary/60"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-300">
                Author
                <input
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white outline-none focus:border-primary/60"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-300">
                Reading time
                <input
                  value={readingTime}
                  onChange={(e) => setReadingTime(e.target.value)}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white outline-none focus:border-primary/60"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-300 sm:col-span-2">
                Tags (comma separated)
                <input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="Product, Sui, API"
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white outline-none focus:border-primary/60"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-300">
                Accent color
                <input
                  type="color"
                  value={accent}
                  onChange={(e) => setAccent(e.target.value)}
                  className="h-10 w-20 rounded-lg border border-white/10 bg-white/[0.03]"
                />
              </label>
            </div>
          </section>

          {/* Media */}
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <h2 className="text-lg font-semibold text-white">Media</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm text-gray-300">
                Cover image
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                  className="text-gray-400"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-gray-300">
                PDF attachment
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  className="text-gray-400"
                />
              </label>
            </div>
          </section>

          {/* Content blocks */}
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <h2 className="text-lg font-semibold text-white">Content</h2>
            <div className="mt-4 flex flex-col gap-4">
              {blocks.map((block, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="rounded-full border border-primary/40 px-3 py-0.5 text-xs font-semibold uppercase text-primary">
                      {block.type}
                    </span>
                    <div className="flex gap-2 text-xs text-gray-400">
                      <button type="button" onClick={() => moveBlock(index, -1)} className="hover:text-white">
                        ↑
                      </button>
                      <button type="button" onClick={() => moveBlock(index, 1)} className="hover:text-white">
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeBlock(index)}
                        className="hover:text-red-400"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="mt-3">
                    {(block.type === "heading" || block.type === "subheading") && (
                      <input
                        value={block.text}
                        onChange={(e) => updateBlock(index, { ...block, text: e.target.value })}
                        placeholder="Section heading"
                        className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white outline-none focus:border-primary/60"
                      />
                    )}
                    {(block.type === "paragraph" || block.type === "callout") && (
                      <textarea
                        value={block.text}
                        onChange={(e) => updateBlock(index, { ...block, text: e.target.value })}
                        rows={3}
                        placeholder={block.type === "callout" ? "Highlighted note" : "Paragraph text"}
                        className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white outline-none focus:border-primary/60"
                      />
                    )}
                    {block.type === "code" && (
                      <textarea
                        value={block.text}
                        onChange={(e) => updateBlock(index, { ...block, text: e.target.value })}
                        rows={5}
                        placeholder="Code / command / response"
                        className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-gray-200 outline-none focus:border-primary/60"
                      />
                    )}
                    {block.type === "table" && (
                      <div className="flex flex-col gap-2">
                        <input
                          value={block.headers}
                          onChange={(e) => updateBlock(index, { ...block, headers: e.target.value })}
                          placeholder="Headers, pipe-separated: Field | Value"
                          className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white outline-none focus:border-primary/60"
                        />
                        <textarea
                          value={block.rowsText}
                          onChange={(e) => updateBlock(index, { ...block, rowsText: e.target.value })}
                          rows={3}
                          placeholder={"One row per line, cells pipe-separated: Network | Sui mainnet"}
                          className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white outline-none focus:border-primary/60"
                        />
                      </div>
                    )}
                    {block.type === "list" && (
                      <textarea
                        value={block.items.join("\n")}
                        onChange={(e) =>
                          updateBlock(index, { ...block, items: e.target.value.split("\n") })
                        }
                        rows={3}
                        placeholder={"One item per line"}
                        className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white outline-none focus:border-primary/60"
                      />
                    )}
                    {block.type === "image" && (
                      <div className="flex flex-col gap-2">
                        {block.url && !block.file && (
                          <p className="truncate text-xs text-gray-500">
                            Using imported image: {block.url}
                          </p>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            updateBlock(index, { ...block, file: e.target.files?.[0] || null })
                          }
                          className="text-gray-400"
                        />
                        <input
                          value={block.caption}
                          onChange={(e) => updateBlock(index, { ...block, caption: e.target.value })}
                          placeholder="Caption (optional)"
                          className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white outline-none focus:border-primary/60"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {BLOCK_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => addBlock(type)}
                  className="rounded-full border border-white/20 px-4 py-1.5 text-sm text-gray-300 transition-colors hover:border-primary/40 hover:text-primary"
                >
                  + {type}
                </button>
              ))}
            </div>
          </section>

          <button
            type="submit"
            disabled={saving}
            className="self-start rounded-full bg-primary px-8 py-3 font-semibold text-space-deep shadow-glow transition-colors hover:bg-primary-400 disabled:opacity-60"
          >
            {saving ? "Publishing…" : "Publish article"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminArticleEditor;
